import * as React from 'react';
import { Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/forms/ui/input';

export type TypeaheadItem = {
  id: string;
  label: string;
};

type AsyncTypeaheadProps = {
  placeholder: string;
  onSelect: (item: TypeaheadItem) => void;
  onClear: () => void;
  searchFn: (search: string) => Promise<TypeaheadItem[]>;
  selectedLabel?: string;
  className?: string;
};

export function AsyncTypeahead({
  placeholder,
  onSelect,
  onClear,
  searchFn,
  selectedLabel,
  className
}: AsyncTypeaheadProps) {
  const [searchText, setSearchText] = React.useState('');
  const [results, setResults] = React.useState<TypeaheadItem[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const searchFnRef = React.useRef(searchFn);
  searchFnRef.current = searchFn;

  // Debounced search
  React.useEffect(() => {
    if (searchText.length < 1) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    const timer = setTimeout(() => {
      setIsLoading(true);
      searchFnRef
        .current(searchText)
        .then(items => {
          setResults(items);
          setIsOpen(true);
        })
        .catch(() => {
          setResults([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchText]);

  // Close on click outside
  React.useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }

  function handleSelect(item: TypeaheadItem) {
    setIsOpen(false);
    setSearchText('');
    setResults([]);
    onSelect(item);
  }

  function handleClear() {
    setSearchText('');
    setResults([]);
    setIsOpen(false);
    onClear();
  }

  if (selectedLabel) {
    return (
      <div ref={containerRef} className={cn('relative', className)}>
        <div className="border-input bg-transparent shadow-xs flex h-9 w-full items-center justify-between rounded-md border px-3 py-1 text-sm">
          <span className="truncate">{selectedLabel}</span>
          <button
            type="button"
            onClick={handleClear}
            className="text-muted-foreground hover:text-foreground ml-2 shrink-0">
            <X className="size-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <Input
          type="text"
          placeholder={placeholder}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onFocus={() => {
            if (searchText.length >= 1 && results.length > 0) {
              setIsOpen(true);
            }
          }}
          onKeyDown={handleKeyDown}
        />
        {isLoading ? (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <Loader2 className="text-muted-foreground size-4 animate-spin" />
          </div>
        ) : null}
      </div>
      {isOpen ? (
        <div className="bg-popover text-popover-foreground absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border shadow-md">
          {results.length === 0 ? (
            <div className="text-muted-foreground px-3 py-2 text-sm">No results</div>
          ) : (
            results.map(item => (
              <button
                key={item.id}
                type="button"
                className="hover:bg-accent hover:text-accent-foreground w-full cursor-default px-3 py-1.5 text-left text-sm"
                onClick={() => handleSelect(item)}>
                {item.label}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
